import { Popconfirm, Space, Typography } from 'antd';
import { getI18n, useTranslation } from 'react-i18next';
import { Automation } from '../../../hooks/adminPage/automation/useCurrentAutomation';
import {
    deleteAutomation,
    disableAutomation,
    enableAutomation,
    runAutomation
} from '../../../services/admin/automations';
import { grispiNotification } from '../../../store/useNotificationStore';
import { tryCatch } from '../../MacroPage/MacroActionButton';

const { Link } = Typography;

const ENABLE_AUTOMATION = 'ENABLE_AUTOMATION';
const DISABLE_AUTOMATION = 'DISABLE_AUTOMATION';
const DELETE_AUTOMATION = 'DELETE_AUTOMATION';
const RUN_AUTOMATION = 'RUN_AUTOMATION';

type ActionFunction = (automationDefinition: Automation, callback: () => Promise<void>) => Promise<void>;

const enableAutomationAction: ActionFunction = async (automationDefinition, callback) => {
    grispiNotification('loading', getI18n().t('enabling', { ns: 'admin' }), ENABLE_AUTOMATION);
    tryCatch(async () => {
        await enableAutomation(automationDefinition.id);
        grispiNotification('success', getI18n().t('enabled', { ns: 'admin' }), ENABLE_AUTOMATION);
        await callback();
    }, ENABLE_AUTOMATION);
};

const disableAutomationAction: ActionFunction = async (automationDefinition, callback) => {
    grispiNotification('loading', getI18n().t('disabling', { ns: 'admin' }), DISABLE_AUTOMATION);

    tryCatch(async () => {
        await disableAutomation(automationDefinition.id);
        grispiNotification('success', getI18n().t('disabled', { ns: 'admin' }), DISABLE_AUTOMATION);
        await callback();
    }, DISABLE_AUTOMATION);
};

const deleteAutomationAction: ActionFunction = async (automationDefinition, callback) => {
    grispiNotification('loading', getI18n().t('deleting', { ns: 'admin' }), DELETE_AUTOMATION);
    tryCatch(async () => {
        await deleteAutomation(automationDefinition.id);
        grispiNotification('success', getI18n().t('deleted', { ns: 'admin' }), DELETE_AUTOMATION);
        await callback();
    }, DELETE_AUTOMATION);
};

const runAutomationAction: ActionFunction = async (automationDefinition, callback) => {
    tryCatch(async () => {
        await runAutomation(automationDefinition.id);
        grispiNotification('success', getI18n().t('running', { ns: 'admin' }), RUN_AUTOMATION);
        await callback();
    }, DELETE_AUTOMATION);
};

export default function ActionButton({
    automationDefinition,
    callback,
    runAction = false
}: {
    automationDefinition: Automation;
    callback: () => Promise<void>;
    runAction: boolean;
}) {
    const { t } = useTranslation('admin');

    if (runAction) {
        return (
            <Popconfirm title={t('sureToRun')} onConfirm={() => runAutomationAction(automationDefinition, callback)}>
                <Link onClick={(event) => event.stopPropagation()}>{t('Run')}</Link>
            </Popconfirm>
        );
    }

    return (
        <>
            <Space size="middle">
                {automationDefinition.enabled ? (
                    <Popconfirm
                        title={t('sureToDisable')}
                        onConfirm={() => disableAutomationAction(automationDefinition, callback)}
                    >
                        <Link onClick={(event) => event.stopPropagation()}>{t('ticketField.disable')}</Link>
                    </Popconfirm>
                ) : (
                    <Popconfirm
                        title={t('sureToEnable')}
                        onConfirm={() => enableAutomationAction(automationDefinition, callback)}
                    >
                        <Link onClick={(event) => event.stopPropagation()}>{t('ticketField.enable')}</Link>
                    </Popconfirm>
                )}

                {!automationDefinition.enabled && (
                    <Popconfirm
                        title={t('sureToDelete')}
                        onConfirm={() => deleteAutomationAction(automationDefinition, callback)}
                    >
                        <Link>{t('delete')}</Link>
                    </Popconfirm>
                )}
            </Space>
        </>
    );
}
